import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { CATEGORIES } from '../data/mockData';
import { PropertyCategory, HeatingType, PropertyFeatures } from '../types';
import { client } from '../api/client';
import { useI18n } from '../i18n';
import { useToast } from '../components/common/Toast';

// Heating types constant
const HEATING_TYPES: HeatingType[] = ['CG', 'EG', 'TA', 'Gas', 'Drvo', 'Bez grejanja'];

// Property features for checkboxes - must match NewListingPage keys
const PROPERTY_FEATURES = [
  { key: 'odmahUseljiv', label: 'Odmah useljiv', icon: '🏠' },
  { key: 'uknjizen', label: 'Uknjižen', icon: '📋' },
  { key: 'lift', label: 'Lift', icon: '🛗' },
  { key: 'terasa', label: 'Terasa', icon: '🏡' },
  { key: 'garaza', label: 'Garaža', icon: '🚗' },
  { key: 'parkingMesto', label: 'Parking mesto', icon: '🅿️' },
  { key: 'klima', label: 'Klima', icon: '❄️' },
  { key: 'internet', label: 'Internet', icon: '📶' },
  { key: 'videoNadzor', label: 'Video nadzor', icon: '📹' },
  { key: 'struja', label: 'Struja', icon: '⚡' },
  { key: 'voda', label: 'Voda', icon: '💧' },
  { key: 'kanalizacija', label: 'Kanalizacija', icon: '🚰' },
  { key: 'dozvoljenaGradnja', label: 'Dozvoljena gradnja', icon: '🏗️' },
];

// Konfiguracija polja forme po kategoriji - ista kao u NewListingPage
interface CategoryFormConfig {
  showRooms: boolean;
  showHeating: boolean;
  showSquareMeters: boolean;
  showFloor: boolean;
  showPlotArea: boolean;
  showUtilities: boolean;
  features: (keyof PropertyFeatures)[];
}

const CATEGORY_FORM_CONFIG: Record<string, CategoryFormConfig> = {
  'Kuća': {
    showRooms: true,
    showHeating: true,
    showSquareMeters: true,
    showFloor: false,
    showPlotArea: true,
    showUtilities: true,
    features: ['odmahUseljiv', 'uknjizen', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'struja', 'voda', 'kanalizacija', 'lift']
  },
  'Stan': {
    showRooms: true,
    showHeating: true,
    showSquareMeters: true,
    showFloor: true,
    showPlotArea: false,
    showUtilities: true,
    features: ['odmahUseljiv', 'uknjizen', 'lift', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'struja', 'voda']
  },
  'Poslovni prostor': {
    showRooms: true,
    showHeating: false,
    showSquareMeters: true,
    showFloor: true,
    showPlotArea: false,
    showUtilities: true,
    features: ['lift', 'klima', 'internet', 'garaza', 'parkingMesto', 'videoNadzor', 'struja', 'voda', 'kanalizacija']
  },
  'Plac': {
    showRooms: false,
    showHeating: false,
    showSquareMeters: false,
    showFloor: false,
    showPlotArea: true,
    showUtilities: false,
    features: ['uknjizen', 'struja', 'voda', 'kanalizacija', 'dozvoljenaGradnja']
  },
  'Garaža': {
    showRooms: false,
    showHeating: false,
    showSquareMeters: false,
    showFloor: false,
    showPlotArea: false,
    showUtilities: false,
    features: ['uknjizen', 'videoNadzor']
  }
};

const getFormConfig = (category: string | ''): CategoryFormConfig => {
  if (!category) {
    return {
      showRooms: true,
      showHeating: true,
      showSquareMeters: true,
      showFloor: true,
      showPlotArea: true,
      showUtilities: true,
      features: ['odmahUseljiv', 'uknjizen', 'lift', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'struja', 'voda', 'kanalizacija', 'dozvoljenaGradnja']
    };
  }
  return CATEGORY_FORM_CONFIG[category] || CATEGORY_FORM_CONFIG['Stan'];
};

export function EditListingPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isChecking, user } = useAppStore();
  const { language } = useI18n();
  const toast = useToast();
  
  // Max file sizes
  const MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB per image
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB per video
  const MAX_MEDIA_COUNT = 15; // max 15 media items total
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [images, setImages] = useState<string[]>([]);

  // Original property data (for readonly fields)
  const [originalProperty, setOriginalProperty] = useState<any>(null);

  // Editable form data - sva polja za Ponudu, Potražnju i Aukciju
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    // Ponuda/Aukcija - tačna cena
    price: '',
    // Potražnja - opseg cena
    priceMin: '',
    priceMax: '',
    currency: 'EUR' as 'EUR' | 'RSD',
    // Ponuda/Aukcija - tačne vrednosti
    squareMeters: '',
    rooms: '',
    floor: '',
    totalFloors: '',
    plotArea: '',
    // Potražnja - opsezi
    squareMetersMin: '',
    squareMetersMax: '',
    roomsMin: '',
    roomsMax: '',
    plotAreaMin: '',
    plotAreaMax: '',
    // Zajednička polja
    heating: '' as HeatingType | '',
    heatingTypes: [] as HeatingType[], // Za Potražnju - više tipova grejanja
    utilities: '',
    contactPhone: '',
    contactEmail: '',
    features: {} as Partial<PropertyFeatures>,
    isUrgent: false,
  });

  // Load the property
  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    try {
      const res = await client.api.fetch(`/properties/${id}`);
      const data = await res.json();
      
      if (!data.data) {
        setError('Oglas nije pronađen');
        return;
      }

      const property = data.data;
      
      // Check ownership - convert both to string for comparison
      const propertyUserId = String(property.user_id);
      const currentUserId = String(user?.id);
      
      if (propertyUserId !== currentUserId && user?.userType !== 'admin') {
        setError('Nemate dozvolu za izmenu ovog oglasa');
        return;
      }

      setOriginalProperty(property);
      
      // Odredi da li je ovo Potražnja oglas
      const isPotraznja = property.ad_kind === 'Potražnja';
      
      // Parsiraj tipove grejanja za Potražnju
      let heatingTypesArray: HeatingType[] = [];
      if (isPotraznja && property.heating_types) {
        try {
          heatingTypesArray = typeof property.heating_types === 'string' 
            ? JSON.parse(property.heating_types) 
            : property.heating_types;
        } catch {
          heatingTypesArray = [];
        }
      }
      
      // Set editable fields - različito za Potražnju i Ponudu/Aukciju
      setFormData({
        title: property.title || '',
        description: property.description || '',
        // Ponuda/Aukcija - tačna cena
        price: property.price?.toString() || '',
        // Potražnja - opseg cena
        priceMin: property.price_min?.toString() || '',
        priceMax: property.price_max?.toString() || '',
        currency: property.currency || 'EUR',
        // Ponuda/Aukcija - tačne vrednosti
        squareMeters: property.square_meters?.toString() || '',
        rooms: property.rooms?.toString() || '',
        floor: property.floor?.toString() || '',
        totalFloors: property.total_floors?.toString() || '',
        plotArea: property.plot_area?.toString() || '',
        // Potražnja - opsezi
        squareMetersMin: property.square_meters_min?.toString() || '',
        squareMetersMax: property.square_meters_max?.toString() || '',
        roomsMin: property.rooms_min?.toString() || '',
        roomsMax: property.rooms_max?.toString() || '',
        plotAreaMin: property.plot_area_min?.toString() || '',
        plotAreaMax: property.plot_area_max?.toString() || '',
        // Zajednička polja
        heating: property.heating || '',
        heatingTypes: heatingTypesArray,
        utilities: property.utilities?.toString() || '',
        contactPhone: property.contact_phone || '',
        contactEmail: property.contact_email || '',
        features: {},
        isUrgent: !!property.is_urgent,
      });

      // Load images
      if (property.images) {
        try {
          const imgs = typeof property.images === 'string' ? JSON.parse(property.images) : property.images;
          setImages(Array.isArray(imgs) ? imgs : []);
        } catch {
          setImages([]);
        }
      }

      // Load features
      try {
        const featuresRes = await client.api.fetch(`/properties/${id}/features`);
        const featuresData = await featuresRes.json();
        if (featuresData.data) {
          const featuresObj: Partial<PropertyFeatures> = {};
          featuresData.data.forEach((f: any) => {
            // Handle both string and number values for feature_value
            const isTrue = f.feature_value === '1' || f.feature_value === 'true' || f.feature_value === 1 || f.feature_value === true;
            featuresObj[f.feature_name as keyof PropertyFeatures] = isTrue;
          });
          setFormData(prev => ({ ...prev, features: featuresObj }));
        }
      } catch (e) {
        // Features loading failed, continue without them
      }

    } catch (err) {
      console.error('Error loading property:', err);
      setError('Greška pri učitavanju oglasa');
    } finally {
      setLoading(false);
    }
  };

  // Compress image before upload
  const compressImage = (file: File, maxWidth: number = 1920, quality: number = 0.85): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Read file as data URL
  const readFileAsDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle media upload with compression (images + videos)
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_MEDIA_COUNT - images.length;
    if (remainingSlots <= 0) {
      toast.warning(language === 'sr' 
        ? `Maksimalan broj fajlova je ${MAX_MEDIA_COUNT}` 
        : `Maximum number of files is ${MAX_MEDIA_COUNT}`);
      return;
    }

    const allFiles = Array.from(files);
    if (allFiles.length > remainingSlots) {
      toast.warning(language === 'sr' 
        ? `Mo\u017eete dodati jo\u0161 ${remainingSlots} fajl(ova). Odabrano: ${allFiles.length}` 
        : `You can add ${remainingSlots} more file(s). Selected: ${allFiles.length}`);
    }

    const filesToAdd = allFiles.slice(0, remainingSlots);
    let skippedSize = 0;
    let skippedType = 0;

    for (const file of filesToAdd) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (!isImage && !isVideo) {
        skippedType++;
        continue;
      }

      // Check file size
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (file.size > maxSize) {
        skippedSize++;
        continue;
      }

      try {
        if (isVideo) {
          const dataUrl = await readFileAsDataUrl(file);
          setImages(prev => [...prev, dataUrl].slice(0, MAX_MEDIA_COUNT));
        } else {
          const compressedImage = await compressImage(file);
          setImages(prev => [...prev, compressedImage].slice(0, MAX_MEDIA_COUNT));
        }
      } catch (error) {
        console.error('Error processing file:', error);
        try {
          const dataUrl = await readFileAsDataUrl(file);
          setImages(prev => [...prev, dataUrl].slice(0, MAX_MEDIA_COUNT));
        } catch {
          // Skip this file
        }
      }
    }

    if (skippedSize > 0) {
      toast.error(language === 'sr' 
        ? `${skippedSize} fajl(ova) presko\u010deno - preveliki (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB za slike, ${MAX_VIDEO_SIZE / 1024 / 1024}MB za video)` 
        : `${skippedSize} file(s) skipped - too large (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB for images, ${MAX_VIDEO_SIZE / 1024 / 1024}MB for video)`);
    }
    if (skippedType > 0) {
      toast.error(language === 'sr' 
        ? `${skippedType} fajl(ova) presko\u010deno - nepodr\u017ean format (samo slike i video)` 
        : `${skippedType} file(s) skipped - unsupported format (images and video only)`);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);

    try {
      const isPotraznja = originalProperty?.ad_kind === 'Potražnja';
      
      // Pripremi podatke na osnovu tipa oglasa
      const requestData: Record<string, any> = {
        title: formData.title,
        description: formData.description,
        currency: formData.currency,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        images: images,
        features: formData.features,
        isUrgent: formData.isUrgent || false,
      };
      
      if (isPotraznja) {
        // Za Potražnju - šalji opsege (konvertuj u brojeve)
        requestData.priceMin = formData.priceMin ? parseFloat(formData.priceMin) : null;
        requestData.priceMax = formData.priceMax ? parseFloat(formData.priceMax) : null;
        requestData.squareMetersMin = formData.squareMetersMin ? parseFloat(formData.squareMetersMin) : null;
        requestData.squareMetersMax = formData.squareMetersMax ? parseFloat(formData.squareMetersMax) : null;
        requestData.roomsMin = formData.roomsMin ? parseInt(formData.roomsMin) : null;
        requestData.roomsMax = formData.roomsMax ? parseInt(formData.roomsMax) : null;
        requestData.plotAreaMin = formData.plotAreaMin ? parseFloat(formData.plotAreaMin) : null;
        requestData.plotAreaMax = formData.plotAreaMax ? parseFloat(formData.plotAreaMax) : null;
        requestData.heatingTypes = formData.heatingTypes.length > 0 ? formData.heatingTypes : null;
      } else {
        // Za Ponudu/Aukciju - šalji tačne vrednosti
        requestData.price = parseFloat(formData.price) || 0;
        requestData.squareMeters = formData.squareMeters ? parseFloat(formData.squareMeters) : null;
        requestData.rooms = formData.rooms ? parseInt(formData.rooms) : null;
        requestData.floor = formData.floor ? parseInt(formData.floor) : null;
        requestData.totalFloors = formData.totalFloors ? parseInt(formData.totalFloors) : null;
        requestData.heatingType = formData.heating || null;
        requestData.utilities = formData.utilities ? parseFloat(formData.utilities) : null;
        requestData.plotArea = formData.plotArea ? parseFloat(formData.plotArea) : null;
      }
      
      // Debug log
      console.log('EditListingPage handleSubmit - sending data:', requestData);
      
      const res = await client.api.fetch(`/properties/${id}`, {
        method: 'PUT',
        body: JSON.stringify(requestData)
      });

      const data = await res.json();
      console.log('EditListingPage handleSubmit - response:', data);
      
      if (data.success) {
        navigate('/moji-oglasi');
      } else {
        setError(data.error || 'Greška pri čuvanju izmena');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Greška pri povezivanju sa serverom');
    } finally {
      setSaving(false);
    }
  };

  // Back button
  const BackButton = () => (
    <button
      onClick={() => navigate(-1)}
      className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-4 transition-colors"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span>{language === 'sr' ? 'Nazad' : 'Back'}</span>
    </button>
  );

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Potrebna prijava' : 'Login Required'}
            </h1>
            <button
              onClick={() => navigate('/prijava')}
              className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400"
            >
              {language === 'sr' ? 'Prijavi se' : 'Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !originalProperty) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">{error}</h1>
            <button
              onClick={() => navigate('/moji-oglasi')}
              className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400"
            >
              {language === 'sr' ? 'Moji oglasi' : 'My Listings'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedCategory = CATEGORIES.find(c => c.name === originalProperty?.category);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <BackButton />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 md:p-8"
        >
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
            {language === 'sr' ? 'Izmeni oglas' : 'Edit Listing'}
          </h1>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <button
                  onClick={() => setStep(s)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    step >= s
                      ? 'bg-amber-500 text-slate-900'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                  }`}
                >
                  {s}
                </button>
                {s < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-amber-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Readonly info */}
          {originalProperty && (
            <div className="mb-6 p-4 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                {language === 'sr' ? 'Ova polja se ne mogu menjati:' : 'These fields cannot be changed:'}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-slate-200 dark:bg-slate-600 rounded text-sm">
                  {originalProperty.ad_kind}
                </span>
                {/* Tip oglasa - Prodaja/Izdavanje za Ponudu/Aukciju, Kupovina/Rentiranje za Potražnju */}
                <span className="px-2 py-1 bg-slate-200 dark:bg-slate-600 rounded text-sm">
                  {originalProperty.ad_type || (originalProperty.ad_kind === 'Potražnja' ? 'Kupovina' : 'Prodaja')}
                </span>
                <span className="px-2 py-1 bg-slate-200 dark:bg-slate-600 rounded text-sm">
                  {originalProperty.category}
                </span>
                {originalProperty.subcategory && (
                  <span className="px-2 py-1 bg-slate-200 dark:bg-slate-600 rounded text-sm">
                    {originalProperty.subcategory}
                  </span>
                )}
                <span className="px-2 py-1 bg-slate-200 dark:bg-slate-600 rounded text-sm">
                  📍 {originalProperty.city}, {originalProperty.region}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                {language === 'sr' ? 'Osnovni podaci' : 'Basic Information'}
              </h2>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'sr' ? 'Naslov oglasa *' : 'Listing Title *'}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'sr' ? 'Opis *' : 'Description *'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Price - različito za Potražnju (opseg) i Ponudu/Aukciju (tačna vrednost) */}
              {originalProperty?.ad_kind === 'Potražnja' ? (
                // Za Potražnju - opseg cene (Od - Do)
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Cena (opseg)' : 'Price (range)'}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <input
                        type="number"
                        min="0"
                        value={formData.priceMin}
                        onChange={(e) => setFormData({ ...formData, priceMin: e.target.value })}
                        placeholder={language === 'sr' ? 'Od' : 'From'}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        value={formData.priceMax}
                        onChange={(e) => setFormData({ ...formData, priceMax: e.target.value })}
                        placeholder={language === 'sr' ? 'Do' : 'To'}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'EUR' | 'RSD' })}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="EUR">EUR</option>
                        <option value="RSD">RSD</option>
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                // Za Ponudu i Aukciju - tačna cena
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'sr' ? 'Cena *' : 'Price *'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'sr' ? 'Valuta' : 'Currency'}
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'EUR' | 'RSD' })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="EUR">EUR</option>
                      <option value="RSD">RSD</option>
                    </select>
                  </div>
                </div>
              )}

              <button
                onClick={() => setStep(2)}
                disabled={!formData.title || (originalProperty?.ad_kind !== 'Potražnja' && !formData.price)}
                className="w-full py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 disabled:opacity-50"
              >
                {language === 'sr' ? 'Nastavi' : 'Continue'}
              </button>
            </div>
          )}

          {/* Step 2: Property Details - dinamički na osnovu kategorije i tipa oglasa */}
          {step === 2 && (() => {
            const isPotraznja = originalProperty?.ad_kind === 'Potražnja';
            const formConfig = getFormConfig(originalProperty?.category || '');
            const filteredFeatures = PROPERTY_FEATURES.filter(f => 
              formConfig.features.includes(f.key as keyof PropertyFeatures)
            );
            
            return (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {language === 'sr' ? 'Detalji nekretnine' : 'Property Details'}
                </h2>

                {isPotraznja ? (
                  // Za Potražnju - opsezi
                  <div className="space-y-4">
                    {/* Kvadratura - opseg */}
                    {formConfig.showSquareMeters && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Kvadratura (m²) - opseg' : 'Area (m²) - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="0"
                            value={formData.squareMetersMin}
                            onChange={(e) => setFormData({ ...formData, squareMetersMin: e.target.value })}
                            placeholder={language === 'sr' ? 'Od' : 'From'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                          <input
                            type="number"
                            min="0"
                            value={formData.squareMetersMax}
                            onChange={(e) => setFormData({ ...formData, squareMetersMax: e.target.value })}
                            placeholder={language === 'sr' ? 'Do' : 'To'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Broj soba - opseg */}
                    {formConfig.showRooms && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Broj soba - opseg' : 'Rooms - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="0"
                            value={formData.roomsMin}
                            onChange={(e) => setFormData({ ...formData, roomsMin: e.target.value })}
                            placeholder={language === 'sr' ? 'Od' : 'From'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                          <input
                            type="number"
                            min="0"
                            value={formData.roomsMax}
                            onChange={(e) => setFormData({ ...formData, roomsMax: e.target.value })}
                            placeholder={language === 'sr' ? 'Do' : 'To'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Površina placa - opseg */}
                    {formConfig.showPlotArea && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Površina placa (m²) - opseg' : 'Plot Area (m²) - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="0"
                            value={formData.plotAreaMin}
                            onChange={(e) => setFormData({ ...formData, plotAreaMin: e.target.value })}
                            placeholder={language === 'sr' ? 'Od' : 'From'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                          <input
                            type="number"
                            min="0"
                            value={formData.plotAreaMax}
                            onChange={(e) => setFormData({ ...formData, plotAreaMax: e.target.value })}
                            placeholder={language === 'sr' ? 'Do' : 'To'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Tipovi grejanja - višestruki izbor za Potražnju */}
                    {formConfig.showHeating && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Prihvatljivi tipovi grejanja' : 'Acceptable heating types'}
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {HEATING_TYPES.map((h) => (
                            <label key={h} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.heatingTypes.includes(h)}
                                onChange={(e) => {
                                  const newTypes = e.target.checked
                                    ? [...formData.heatingTypes, h]
                                    : formData.heatingTypes.filter(t => t !== h);
                                  setFormData({ ...formData, heatingTypes: newTypes });
                                }}
                                className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                              />
                              <span className="text-sm text-slate-700 dark:text-slate-300">{h}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Za Ponudu/Aukciju - tačne vrednosti
                  <div className="space-y-4">
                    {formConfig.showSquareMeters && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Kvadratura (m²)' : 'Square Meters'}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.squareMeters}
                            onChange={(e) => setFormData({ ...formData, squareMeters: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        {formConfig.showRooms && (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                              {language === 'sr' ? 'Broj soba' : 'Rooms'}
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={formData.rooms}
                              onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
                              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {formConfig.showFloor && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Sprat' : 'Floor'}
                          </label>
                          <select
                            value={formData.floor}
                            onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Izaberite sprat' : 'Select floor'}</option>
                            <option value="-3">{language === 'sr' ? '-2 ili niže (Duboki podrum)' : '-2 or lower (Deep basement)'}</option>
                            <option value="-2">-2 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="-1">-1 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="S">{language === 'sr' ? 'Suteren (S)' : 'Semi-basement (S)'}</option>
                            <option value="P">{language === 'sr' ? 'Prizemlje (P)' : 'Ground floor (P)'}</option>
                            <option value="M">{language === 'sr' ? 'Međusprat (M)' : 'Mezzanine (M)'}</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8+</option>
                            <option value="Pk">{language === 'sr' ? 'Potkrovlje (Pk)' : 'Attic (Pk)'}</option>
                            <option value="Mansarda">{language === 'sr' ? 'Mansarda' : 'Mansard'}</option>
                            <option value="Tavan">{language === 'sr' ? 'Tavan' : 'Loft'}</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Ukupno spratova' : 'Total floors'}
                          </label>
                          <select
                            value={formData.totalFloors}
                            onChange={(e) => setFormData({ ...formData, totalFloors: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Izaberite' : 'Select'}</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8</option>
                            <option value="9">9</option>
                            <option value="10">10</option>
                            <option value="11">11+</option>
                          </select>
                        </div>
                      </>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      {formConfig.showHeating && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Grejanje' : 'Heating'}
                          </label>
                          <select
                            value={formData.heating}
                            onChange={(e) => setFormData({ ...formData, heating: e.target.value as HeatingType })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Izaberite' : 'Select'}</option>
                            <option value="CG">{language === 'sr' ? 'Centralno grejanje' : 'Central heating'}</option>
                            <option value="EG">{language === 'sr' ? 'Etažno grejanje' : 'Floor heating'}</option>
                            <option value="TA">{language === 'sr' ? 'TA peć' : 'Storage heater'}</option>
                            <option value="Gas">{language === 'sr' ? 'Gas' : 'Gas'}</option>
                            <option value="Drvo">{language === 'sr' ? 'Drvo' : 'Wood'}</option>
                            <option value="Bez grejanja">{language === 'sr' ? 'Bez grejanja' : 'No heating'}</option>
                          </select>
                        </div>
                      )}
                      {formConfig.showUtilities && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Režije (EUR/mesec)' : 'Utilities (EUR/month)'}
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={formData.utilities}
                            onChange={(e) => setFormData({ ...formData, utilities: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      )}
                    </div>

                    {formConfig.showPlotArea && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Površina placa (m²)' : 'Plot Area (m²)'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.plotArea}
                          onChange={(e) => setFormData({ ...formData, plotArea: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Features - filtrirane na osnovu kategorije */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    {language === 'sr' ? 'Karakteristike' : 'Features'}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filteredFeatures.map((feature) => (
                      <label
                        key={feature.key}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!!formData.features[feature.key as keyof PropertyFeatures]}
                          onChange={(e) => setFormData({
                            ...formData,
                            features: { ...formData.features, [feature.key]: e.target.checked }
                          })}
                          className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {feature.icon} {feature.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* HITNO / Urgent Toggle */}
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.isUrgent || false}
                      onChange={(e) => setFormData({ ...formData, isUrgent: e.target.checked })}
                      className="w-5 h-5 rounded border-red-300 dark:border-red-600 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="flex items-center gap-2 text-base font-bold text-red-600 dark:text-red-400">
                        🔥 {language === 'sr' ? 'HITNO!' : 'URGENT!'}
                      </span>
                      <p className="text-sm text-red-500 dark:text-red-400 mt-1">
                        {language === 'sr' 
                          ? 'Označite ako je oglas hitan - olakšava pretragu hitnih oglasa' 
                          : 'Mark if listing is urgent - makes it easier to find urgent listings'}
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    {language === 'sr' ? 'Nazad' : 'Back'}
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="flex-1 py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400"
                  >
                    {language === 'sr' ? 'Nastavi' : 'Continue'}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Step 3: Images & Contact */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                {language === 'sr' ? 'Slike i kontakt' : 'Images & Contact'}
              </h2>

              {/* Images & Videos */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'sr' ? `Slike i video (${images.length}/${MAX_MEDIA_COUNT})` : `Images & Video (${images.length}/${MAX_MEDIA_COUNT})`}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  {images.map((img, index) => (
                    <div key={index} className="relative group">
                      {img.startsWith('data:video/') ? (
                        <div className="w-full h-24 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                          <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      ) : (
                        <img
                          src={img}
                          alt={`Image ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                      )}
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {images.length < MAX_MEDIA_COUNT && (
                    <label className="w-full h-24 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-amber-500">
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <span className="text-slate-400 text-2xl">+</span>
                      <span className="text-xs text-slate-400 mt-1">
                        {language === 'sr' ? 'Slike/Video' : 'Images/Video'}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Kontakt telefon' : 'Contact Phone'}
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Kontakt email' : 'Contact Email'}
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  {language === 'sr' ? 'Nazad' : 'Back'}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex-1 py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 disabled:opacity-50"
                >
                  {saving 
                    ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') 
                    : (language === 'sr' ? 'Sačuvaj izmene' : 'Save Changes')}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
